<script>
  import { onMount, createEventDispatcher } from "svelte";
  import { copyToClipboard } from "overline";
  import { tooltip } from "svelte-utilities";

  import XIcon from "@local/assets/icons/x.svg";
  import EllipsisIcon from "@local/assets/icons/ellipsis.svg";
  import RulerIcon from "@local/assets/icons/ruler.svg";
  import eb from "#src/extensions/event-bus.js";
  import {
    currentTool,
    useDocMeasurementScale,
    docMeasurementScale,
    preserveDimensions,
  } from "#src/stores/ui.js";
  import DimensionLinearIcon from "@local/assets/icons/dimension-linear.svg";
  import DimensionRectangularIcon from "@local/assets/icons/dimension-rectangular.svg";
  import SelectInput from "#src/lib/sidebar/SelectInput.svelte";
  import dimSettings from "@local/extensions/utilities/doc-dim-settings.js";
  import { lengthDim, rectangleDims } from "#src/extensions/dimension-from-points.js";
  import { scaleOptions } from "@local/extensions/utilities/scales.js";
  import { alerts } from "#src/stores/alerts.js";

  export let multiclicks;
  export let currentMousePos;
  export let settings;
  export let tempTool;
  export let shiftDown = false;
  export let doc;
  export let hoveredPageId;

  const dispatch = createEventDispatcher();

  let visible = false;
  let showScaleOptions = $useDocMeasurementScale;

  $: ds = dimSettings(settings);
  $: tool = tempTool || $currentTool;
  $: scaleLabels = scaleOptions.reduce((acc, option) => {
    acc[option.value] = option.label;
    return acc;
  }, {});
  $: pageScale = doc.data.pages[hoveredPageId]?.scale;

  function toggleVisible() {
    eb.dispatch("close-doc-panels", "ruler");
    showScaleOptions = $useDocMeasurementScale;
    visible = !visible;
    multiclicks = { tool: null, clicks: [] };

    if (!visible) {
      $currentTool = "select";
    } else {
      $currentTool = "dimension-linear";
    }
  }

  function closePanel(except) {
    if (except === "ruler") return;
    visible = false;
    multiclicks = { tool: null, clicks: [] };
    $currentTool = "select";
  }

  function startLinearDim() {
    multiclicks = { tool: null, clicks: [] };
    $currentTool = "dimension-linear";
  }

  function startRectangularDim() {
    multiclicks = { tool: null, clicks: [] };
    $currentTool = "dimension-rectangular";
  }

  function getLength(clicks, currentMousePos, shiftDown) {
    if (clicks.length === 0) return null;

    const ptA = clicks[0];
    const ptB = clicks[1] ?? currentMousePos;

    if (!ptB) return null;

    const scale = $useDocMeasurementScale ? $docMeasurementScale : doc.data.pages[hoveredPageId]?.scale;
    if (!scale) return null;

    return lengthDim(ptA, ptB, scale, ds, shiftDown);
  }

  function getRectangleDims(clicks, currentMousePos) {
    if (clicks.length === 0) return null;

    const ptA = clicks[0];
    const ptB = clicks[1] ?? currentMousePos;

    if (!ptB) return null;

    const scale = $useDocMeasurementScale ? $docMeasurementScale : doc.data.pages[hoveredPageId]?.scale;
    if (!scale) return null;

    return rectangleDims(ptA, ptB, scale, ds);
  }

  function copyValue(value) {
    copyToClipboard(value);
    alerts.add({ type: "ok", message: `Copied to clipboard!`, timeout: 2000 });
  }

  onMount(() => {
    eb.on("open-ruler", toggleVisible);
    eb.on("close-doc-panels", closePanel);

    return () => {
      eb.unsubscribe("open-ruler", toggleVisible);
      eb.unsubscribe("close-doc-panels", closePanel);
    };
  });
</script>

{#if visible}
  <!-- Positioning belongs to DocCornerPanels (the shared top-right stack);
       this is just the card. -->
  <div class="w-72 pointer-events-auto text-xs">
    <div class="bg-white rounded border shadow-lg w-full">
      <div class="p-2 flex items-center border-b">
        <div class="grow">Measure Drawing</div>
        <button on:click={toggleVisible} class="rounded p-1 hover:bg-gray-200">
          <XIcon />
        </button>
      </div>
      <div class="p-2 space-y-1">
        <div>
          <div class="flex gap-2 justify-between items-center">
            <div class="grow flex gap-2 p-2">
              {#if showScaleOptions}
                <input type="radio" name="Page Scale" value={false} bind:group={$useDocMeasurementScale} />
              {/if}
              <div class="text-gray-400">Page Scale</div>
              <div>{scaleLabels[pageScale] || "None"}</div>
            </div>
            <button
              class="p-1 hover:bg-gray-200 rounded"
              on:click={() => (showScaleOptions = !showScaleOptions)}>
              <EllipsisIcon />
            </button>
          </div>
          {#if showScaleOptions}
            <div class="flex gap-2 items-center pl-2">
              <input type="radio" name="Unique Scale" value={true} bind:group={$useDocMeasurementScale} />
              <div class="whitespace-nowrap text-gray-400">Unique Scale</div>
              <SelectInput label={null} options={scaleOptions} bind:value={$docMeasurementScale} />
            </div>
          {/if}
        </div>
        <div class="flex justify-between items-center">
          <div class="flex gap-2 items-center px-2">
            <button
              use:tooltip={{ text: "Linear Dimension" }}
              on:click={startLinearDim}
              class="p-1 border hover:bg-gray-200 text-gray-500 hover:text-black"
              class:text-black={tool === "dimension-linear"}
              class:border-black={tool === "dimension-linear"}>
              <DimensionLinearIcon />
            </button>
            <button
              use:tooltip={{ text: "Rectangular Dimension" }}
              on:click={startRectangularDim}
              class="p-1 border hover:bg-gray-200 text-gray-500 hover:text-black"
              class:text-black={tool === "dimension-rectangular"}
              class:border-black={tool === "dimension-rectangular"}>
              <DimensionRectangularIcon />
            </button>
            <button
              use:tooltip={{ text: "Define Page Scale" }}
              class="p-1 border hover:bg-gray-200 text-gray-500 hover:text-black"
              class:border-black={tool === "pick-scale-dim"}
              class:text-black={tool === "pick-scale-dim"}
              on:click={() => dispatch("pick-dimension")}>
              <RulerIcon />
            </button>
          </div>
          <div class="flex gap-2 items-center px-2">
            <div>Save in Document</div>
            <input type="checkbox" bind:checked={$preserveDimensions} />
          </div>
        </div>
        {#if multiclicks.clicks.length}
          {#if multiclicks.tool === "dimension-linear"}
            {@const len = getLength(multiclicks.clicks, currentMousePos, shiftDown)}
            {#if len}
              <div class="p-2">
                <span class="text-gray-500">L:</span>
                <button
                  class="font-bold rounded p-1 border border-transparent hover:border-gray-300"
                  on:click={() => copyValue(len)}>
                  {len}
                </button>
              </div>
            {/if}
          {:else if multiclicks.tool === "dimension-rectangular"}
            {@const dims = getRectangleDims(multiclicks.clicks, currentMousePos)}
            {#if dims}
              <div class="p-2 font-bold flex gap-2 flex-wrap">
                <div>
                  <span class="text-gray-500">W:</span>
                  <button
                    class="font-bold rounded p-1 border border-transparent hover:border-gray-300"
                    on:click={() => copyValue(dims[0])}>
                    {dims[0]}
                  </button>
                </div>
                <div>
                  <span class="text-gray-500">H:</span>
                  <button
                    class="font-bold rounded p-1 border border-transparent hover:border-gray-300"
                    on:click={() => copyValue(dims[1])}>
                    {dims[1]}
                  </button>
                </div>
              </div>
            {/if}
          {/if}
        {/if}
      </div>
    </div>
  </div>
{/if}
